import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { MenuCategoryDTO } from '../../../../types';
import { ArrowLeft, SquarePen, Trash2, LayoutGrid } from 'lucide-react';
import '../RestaurantPage.css';

const MenuCategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<MenuCategoryDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await menuCategoryService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setItem(d);
    } catch {
      setError(t('restaurant.menuCategory.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!window.confirm(t('restaurant.menuCategory.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await menuCategoryService.delete(item.id);
      navigate('/restaurant/menu-categories');
    } catch {
      setError(t('restaurant.menuCategory.failedToDelete'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-page rest-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="ims-page rest-page">
          <div className="ims-alert ims-alert-error">{error ?? t('restaurant.menuCategory.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-detail-page rest-detail-page">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="ims-hero rest-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/restaurant/menu-categories')}>
              <ArrowLeft size={14} />
              {t('restaurant.menuCategory.title')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{item.name}</h1>
              {item.typeName && (
                <p className="ims-hero-supplier">{item.typeName}</p>
              )}
              <div className="ims-hero-badges">
                <span className={`ims-hero-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                  {item.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="ims-btn ims-btn-secondary"
                  onClick={() => navigate(`/restaurant/menu-categories/${item.id}/edit`)}
                >
                  <SquarePen size={14} style={{ marginInlineEnd: 4 }} />
                  {t('common.edit')}
                </button>
                <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                  <Trash2 size={14} style={{ marginInlineEnd: 4 }} />
                  {deleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
              <span className="ims-hero-date">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBlock: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {/* ── Details ──────────────────────────────────────────────────── */}
        <div className="ims-detail-body">
          <div className="ims-detail-main">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <LayoutGrid size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('restaurant.menuCategory.details')}
                </span>
              </div>
              <div className="ims-card-body">
                <div className="rest-fields-grid">
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuCategory.name')}</div>
                    <div className="rest-field-value strong">{item.name}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuCategory.type')}</div>
                    <div className="rest-field-value">
                      {item.typeName ? (
                        <span className="ims-badge rest-badge-type">{item.typeName}</span>
                      ) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuType.sortOrder')}</div>
                    <div className="rest-field-value">{item.sortOrder}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('common.status')}</div>
                    <div className="rest-field-value">
                      <span className={`ims-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                        {item.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>
                  <div className="rest-field-full">
                    <div className="rest-field-label">{t('common.description')}</div>
                    <div className="rest-field-value">{item.description || '—'}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('common.createdAt')}</div>
                    <div className="rest-field-value">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MenuCategoryDetailPage;
